import {
  TravelDocumentSalesLiteModel,
  TravelDocumentSalesModel,
  getTravelDocumentSalesKey,
  getTravelDocumentsSalesKey,
  useGetTravelDocumentSales,
  useGetTravelDocumentsSales,
} from 'api-hooks/sale/travel-document';
import { OnChangeParams, Option } from 'baseui/select';
import { NavigationRoutes } from 'common/constants';
import { SaleStatusEnum } from 'common/constants/enum';
import { QueryTransformer } from 'common/helpers/common';
import { transformLabel } from 'common/utils/string';
import { Input } from 'components/elements';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useNavigation from 'hooks/use-navigation';
import useSelectInputHelper from 'hooks/use-select-input-helper';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

function transformer(
  item: TravelDocumentSalesLiteModel | TravelDocumentSalesModel,
): Option {
  return {
    id: item.id,
    label: transformLabel([item.invoiceNumber, item.customer.name]),
    extra: item,
  };
}

interface Props {
  name: string;
  required?: boolean;
  label?: string;
  placeholder?: string;
  onClose?: () => void;
  onAfterChange?: (value?: OnChangeParams) => void;
  onAfterDetailChange?: (value: TravelDocumentSalesModel) => void;
  noMargin?: boolean;
  fieldType?: 'control' | 'uncontrol';
  disabled?: boolean;
  isSales?: boolean;
}

export default function TravelDocumentInvoiceSelectInput(props: Props) {
  const { t } = useTranslation();
  const {
    label = t('common:travel_document_sales'),
    placeholder = t('common:select_travel_document_sales'),
    disabled,
  } = props;
  const { control, setValue } = useFormContext<any>();
  const value = useWatch({ control, name: props.name });

  const { navigate } = useNavigation();

  const permission = useGetCrudPermission('document-sales');
  const { overrides, ...selectProps } = useSelectInputHelper({
    value,
    onSelectItem: (value) => {
      setValue(props.name, value[0]?.id || '', {
        shouldTouch: true,
        shouldValidate: true,
      });
    },
    useListQueryHook: useGetTravelDocumentsSales,
    useDetailLazyQueryHook: useGetTravelDocumentSales,
    listKeyFunction: getTravelDocumentsSalesKey,
    detailKeyFunction: getTravelDocumentSalesKey,
    getMemoizedListVariables: React.useCallback(
      (page, q) => ({
        params: {
          q,
          page,
          filter: {
            status: props.isSales
              ? `${SaleStatusEnum.Active}`
              : `${SaleStatusEnum.Active}|${SaleStatusEnum.OpenTicket}|${SaleStatusEnum.Completed}|${SaleStatusEnum.Waiting}`,
            is_completed: false,
          },
          selection: 'on',
        },
      }),
      [props.isSales],
    ),
    renderCreate: permission.create,
    onClickCreate: React.useCallback(() => {
      navigate(NavigationRoutes.TravelDocumentSalesCreate);
      props.onClose?.();
    }, [navigate, props]),
    getMemoizedDetailVariables: React.useCallback(
      (value: string) => ({
        id: value,
      }),
      [],
    ),
    listTransformer: React.useCallback((result) => {
      return result.data.map((v) => transformer(v));
    }, []),
    detailTransformer: React.useCallback((result) => {
      const newResult = QueryTransformer(result, TravelDocumentSalesModel);
      result && props.onAfterDetailChange?.(newResult.data);
      return transformer(newResult.data);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []),
    paginationTransformer: React.useCallback((result) => {
      return result.meta;
    }, []),
  });

  return (
    <Input
      type="select"
      name={props.name}
      required={props.required}
      label={label}
      placeholder={placeholder}
      onAfterChange={props.onAfterChange}
      noMargin={props.noMargin}
      fieldType={props.fieldType}
      disabled={disabled}
      overrides={overrides}
      {...selectProps}
    />
  );
}
